import {
  type DefaultContext,
  type OperationVariables,
  useMutation,
} from "@apollo/client";
import { Alert, Button, ButtonGroup } from "@blueprintjs/core";
import { useToaster } from "@package/ui-components";
import {
  EnablePostDocument,
  useValidateGqlModelMutation,
} from "@package/ui-gql/index-data-access";
import { type ValidationError } from "class-validator";
import { type DocumentNode } from "graphql";
import { useEffect, useState } from "react";

const TIMEOUT_MS_INFLIGHT = 10000;
const TIMEOUT_MS_COMPLETE = 3000;

export type SaveToolbarProps = {
  context?: DefaultContext;
  deleteSupported?: boolean;
  documentCreate: DocumentNode;
  documentDelete?: DocumentNode;
  documentUpdate: DocumentNode;
  isNew: boolean;
  refetchQueries?: DocumentNode[];
  title: string;
  validationModel?: string;
  variablesCreate: OperationVariables;
  variablesDelete?: OperationVariables;
  variablesUpdate: OperationVariables;
  onCanceled?: () => void;
  onCreated?: (id?: string) => void;
  onDeleted?: (id?: string) => void;
  onInflight?: (inflight: boolean) => void;
  onValidationFailed?: (errors: ValidationError[]) => void;
};

export function SaveToolbar({
  context,
  deleteSupported = true,
  documentCreate,
  documentDelete = EnablePostDocument, // Random document when delete is not supported
  documentUpdate,
  isNew,
  refetchQueries,
  title,
  validationModel,
  variablesCreate,
  variablesDelete,
  variablesUpdate,
  onCanceled,
  onCreated,
  onDeleted,
  onInflight,
  onValidationFailed,
}: SaveToolbarProps) {
  const { toaster } = useToaster();

  const [isInflight, setIsInflight] = useState<boolean>(false);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState<boolean>(false);

  const [createItem] = useMutation(documentCreate);
  const [deleteItem] = useMutation(documentDelete);
  const [updateItem] = useMutation(documentUpdate);
  const [validateGqlModel] = useValidateGqlModelMutation();

  useEffect(
    function effectIsInflight() {
      onInflight?.(isInflight);
    },
    [isInflight, onInflight],
  );

  return (
    <div className="flex flex-row w-full">
      <div className="grow">
        <ButtonGroup className="pr-2">
          <Button
            disabled={isInflight}
            intent="primary"
            text="Save"
            className="min-w-20"
            onClick={async () => {
              setIsInflight(true);

              let failed = false;
              const key = toaster?.show({
                message: `Saving ${title}...`,
                intent: "none",
                isCloseButtonShown: false,
                timeout: TIMEOUT_MS_INFLIGHT,
                icon: "floppy-disk",
              });

              if (validationModel) {
                const validation = await validateGqlModel({
                  variables: {
                    action: validationModel,
                    data: isNew ? variablesCreate : variablesUpdate,
                  },
                });

                if (validation.data?.data.errors?.length) {
                  failed = true;
                  onValidationFailed?.(validation.data?.data.errors);
                }
              }

              if (!failed) {
                if (isNew) {
                  await createItem({
                    context,
                    refetchQueries,
                    variables: variablesCreate,
                    onError: () => {
                      failed = true;
                    },
                    onCompleted: (data) => {
                      setIsInflight(false);
                      onCreated?.(data?.data?.id);
                    },
                  });
                } else {
                  await updateItem({
                    context,
                    refetchQueries,
                    variables: variablesUpdate,
                    onError: () => {
                      failed = true;
                    },
                    onCompleted: () => {
                      setIsInflight(false);
                    },
                  });
                }
              }

              if (key) {
                toaster?.show(
                  {
                    message: failed
                      ? `Failed saving ${title}`
                      : `Successfully saved ${title}`,
                    intent: failed ? "danger" : "success",
                    icon: failed ? "remove" : "tick",
                    timeout: TIMEOUT_MS_COMPLETE,
                  },
                  key,
                );
              }

              setIsInflight(false);
            }}
          />
        </ButtonGroup>
        <ButtonGroup>
          <Button
            disabled={isInflight}
            intent="none"
            minimal
            outlined
            text="Cancel"
            className="min-w-20"
            onClick={() => onCanceled?.()}
          />
        </ButtonGroup>
      </div>
      <div>
        {!isNew && deleteSupported && (
          <ButtonGroup>
            <Button
              disabled={isInflight}
              minimal
              intent="danger"
              text="Delete"
              className="min-w-20"
              onClick={() => setIsDeleteAlertOpen(true)}
            />
          </ButtonGroup>
        )}
      </div>
      <Alert
        cancelButtonText="Cancel"
        canEscapeKeyCancel
        canOutsideClickCancel
        confirmButtonText="Delete"
        icon="trash"
        intent="danger"
        isOpen={isDeleteAlertOpen}
        onCancel={() => setIsDeleteAlertOpen(false)}
        onConfirm={async () => {
          setIsInflight(true);

          await deleteItem({
            context,
            notifyOnNetworkStatusChange: true,
            refetchQueries,
            variables: variablesDelete,
            onCompleted: () => {
              setIsInflight(false);
              onDeleted?.();

              toaster?.show({
                icon: "tick",
                intent: "success",
                message: `Deleted ${title}`,
                timeout: 3000,
              });
            },
          });

          setIsInflight(false);
        }}
      >
        <p>Are you sure you want to delete this resource?</p>
      </Alert>
    </div>
  );
}
